import { useAuthStore } from "@/store/auth-store";
import { useStudioStore } from "@/store/studio-store";

type ApiOptions = RequestInit & {
  skipUnauthorizedLogout?: boolean;
};

/**
 * Erreur API qui conserve le statut HTTP et le corps de la reponse.
 * Sans cela, `code` et `fieldErrors` renvoyes par le serveur etaient perdus et
 * l'utilisateur ne voyait qu'un message generique.
 */
export class ApiError extends Error {
  status: number;
  code?: string;
  fieldErrors?: Record<string, string>;
  body?: unknown;

  constructor(
    message: string,
    status: number,
    body?: {
      code?: string;
      fieldErrors?: Record<string, string>;
      [key: string]: unknown;
    } | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code;
    this.fieldErrors = body?.fieldErrors;
    this.body = body;
  }
}

export async function api(path: string, options: ApiOptions = {}) {
  const auth = useAuthStore.getState();
  const { studioId } = useStudioStore.getState();

  const headers = new Headers(options.headers);
  let requestPath = path;

  if (
    studioId &&
    path.startsWith("/api/widget/") &&
    typeof window !== "undefined"
  ) {
    const url = new URL(path, window.location.origin);

    if (!url.searchParams.has("studioId")) {
      url.searchParams.set("studioId", studioId);
    }

    requestPath = `${url.pathname}${url.search}`;
  }

  // Only set Content-Type if we actually send JSON
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (auth.token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${auth.token}`);
  }

  const res = await fetch(requestPath, {
    ...options,
    headers,
    credentials: options.credentials ?? "include",
  });

  if (res.status === 401 && !options.skipUnauthorizedLogout) {
    auth.logout();
  }

  if (!res.ok) {
    const contentType = res.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      const body = await res.json().catch(() => null);
      const message = body?.error ?? body?.message;
      if (message) throw new ApiError(message, res.status, body);
    }
    throw new ApiError(`API error: ${res.status}`, res.status, null);
  }

  return res.json();
}

export const swrFetcher = async (path: string) => {
  return api(path);
};
